// Light/Dark/System theme toggle. `data-theme` on <html> is the only
// mechanism — app.css defines dark values for the existing --tym-* custom
// properties under `[data-theme="dark"]`, so no component is restyled here.
//
// The actual "no flash on load" work happens in index.html's inline script,
// which reads the same localStorage key and applies `data-theme` before the
// stylesheet paints. This module re-applies it (cheap, idempotent) so the
// two stay in lockstep, and owns the matchMedia listener so "System" reacts
// live to OS changes.

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'pq-theme';

function isThemeMode(v: string | null): v is 'light' | 'dark' {
  return v === 'light' || v === 'dark';
}

const media = window.matchMedia('(prefers-color-scheme: dark)');

function resolve(mode: ThemeMode): 'light' | 'dark' {
  return mode === 'system' ? (media.matches ? 'dark' : 'light') : mode;
}

function apply(mode: ThemeMode) {
  document.documentElement.setAttribute('data-theme', resolve(mode));
}

class ThemeState {
  mode = $state<ThemeMode>(isThemeMode(localStorage.getItem(STORAGE_KEY)) ? (localStorage.getItem(STORAGE_KEY) as ThemeMode) : 'system');

  set(mode: ThemeMode) {
    this.mode = mode;
    if (mode === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, mode);
    apply(mode);
  }
}

export const theme = new ThemeState();
apply(theme.mode);

// "System" must react live to OS-level dark/light changes, not just at load.
media.addEventListener('change', () => {
  if (theme.mode === 'system') apply('system');
});
